import React, { useState, useEffect } from "react";
import { ClipLoader } from "react-spinners";
import { motion } from "framer-motion";

const Loader = ({ 
  isLoading = true, 
  text = "Loading...", 
  showProgress = true, 
  duration = 3000,
  size = 50,
  color = "#3B82F6"
}) => {
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    if (!showProgress || !isLoading) return;

    const interval = setInterval(() => {
      setProgress(prev => {
        if (prev >= 100) {
          clearInterval(interval);
          return 100;
        }
        return prev + 1;
      });
    }, duration / 100);

    return () => clearInterval(interval);
  }, [showProgress, isLoading, duration]);

  useEffect(() => {
    if (!isLoading) {
      setProgress(0);
    }
  }, [isLoading]);

  if (!isLoading) return null;

  return (
    <motion.div 
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[9999] bg-black/80 flex justify-center items-center"
    >
      <div className="flex flex-col items-center justify-center space-y-6">
        {/* Spinner */}
        <motion.div
          initial={{ scale: 0.5 }}
          animate={{ scale: 1 }}
          transition={{ duration: 0.5 }}
        >
          <ClipLoader
            color={color}
            loading={isLoading}
            size={size}
            aria-label="Loading Spinner"
            data-testid="loader"
          />
        </motion.div>

        {/* Loading Text */}
        <motion.p 
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="text-white text-xl font-medium tracking-wide"
        >
          {text}
        </motion.p>

        {/* Progress Bar and Percentage */}
        {showProgress && (
          <motion.div 
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.5 }}
            className="flex flex-col items-center space-y-3"
          >
            {/* Progress Percentage */}
            <motion.span 
              key={progress}
              initial={{ scale: 0.8 }}
              animate={{ scale: 1 }}
              className="text-white text-2xl font-bold"
            >
              {progress}%
            </motion.span>
            
            {/* Progress Bar */}
            <div className="w-64 h-2 bg-gray-700 rounded-full overflow-hidden">
              <motion.div
                className="h-full bg-gradient-to-r from-blue-500 to-purple-600 rounded-full"
                initial={{ width: 0 }}
                animate={{ width: `${progress}%` }}
                transition={{ duration: 0.3, ease: "easeOut" }}
              />
            </div>
          </motion.div>
        )}
      </div>
    </motion.div>
  );
};

export default Loader;
